import React, { useState, useMemo, useCallback } from 'react';
import ConsultationModal from '../components/ConsultationModal';
import { FaCheck, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import '../styles/pages/Home.css';
import './PricingNew.css';
import useSeo from '../hooks/useSeo';

// ============================================================================
// SIMPLIFIED DATA - 2 BUNDLES
// ============================================================================

const BUNDLES = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Core operations for enterprise field teams',
    ideal: 'Best for: 1-25 users',
    includedAddons: [],
    featureGroups: [
      {
        name: 'Location & Tracking',
        features: ['Real-time GPS tracking', 'Trip detection & history', 'Route visualization']
      },
      {
        name: 'Attendance',
        features: ['Check-in/out with location', 'Break management', 'Attendance reports']
      },
      {
        name: 'Task Management',
        features: ['Create & assign tasks', 'Priority levels', 'Basic task views']
      },
      {
        name: 'Customer Management',
        features: ['Customer database', 'Contact management']
      },
      {
        name: 'Team & Access Control',
        features: ['Departments & teams', 'Supervisor hierarchy', 'Feature access controls']
      },
      {
        name: 'Notifications',
        features: ['Push notifications', 'Email alerts']
      }
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'Full platform with finance and advanced workflows',
    popular: true,
    ideal: 'Best for: 25+ users',
    includedAddons: ['face-recognition', 'pos', 'scheduling'],
    featureGroups: [
      {
        name: 'Plus everything in Starter',
        isInherited: true
      },
      {
        name: 'Advanced Tasks',
        features: ['Subtasks & checklists', 'Kanban, Calendar & Gantt views', 'Task map view', 'Work logs']
      },
      {
        name: 'Accounting & Invoicing',
        features: ['Quotes & estimates', 'Invoice generation', 'Proforma invoices', 'PDF generation & email', 'Recurring invoices (AMC)']
      },
      {
        name: 'Customer Portal',
        features: ['Self-service portal for customers']
      },
      {
        name: 'Orders & Inventory',
        features: ['Order management', 'Public order links', 'Inventory tracking', 'Point of Sale (POS)']
      },
      {
        name: 'Security & Verification',
        features: ['Face recognition attendance', 'Liveness detection', 'Anti-spoofing protection']
      },
      {
        name: 'Scheduling',
        features: ['Calendar & availability', 'Public booking links', 'Google Calendar sync', 'Google Meet integration']
      }
    ]
  }
];

const ADD_ONS = [
  {
    id: 'face-recognition',
    name: 'Face Recognition',
    description: 'Biometric attendance verification with liveness detection',
    features: ['Face enrollment & verification', 'Anti-spoofing protection', 'Re-enrollment workflow']
  },
  {
    id: 'pos',
    name: 'Point of Sale',
    description: 'In-store sales with instant invoicing',
    features: ['POS interface', 'Instant invoices', 'Payment tracking']
  },
  {
    id: 'scheduling',
    name: 'Scheduling Suite',
    description: 'Calendar, bookings, and meeting integrations',
    features: ['Public booking links', 'Google Calendar sync', 'Google Meet integration']
  },
  {
    id: 'kyc',
    name: 'Verification Suite',
    description: 'Employee and company verification workflows with usage-based billing.',
    features: ['Aadhaar, PAN, DL, GSTIN checks', 'Verification audit trail', 'API-based integration options']
  }
];

// ============================================================================
// COMPONENTS
// ============================================================================

function FeatureGroup({ group, isExpanded, onToggle }) {
  if (group.isInherited) {
    return (
      <div className="feature-group inherited">
        <span className="inherited-label">{group.name}</span>
      </div>
    );
  }
  
  return (
    <div className="feature-group">
      <button className="group-header" onClick={onToggle}>
        <span className="group-name">{group.name}</span>
        <span className="group-count">{group.features.length}</span>
        {isExpanded ? <FaChevronUp className="chevron" /> : <FaChevronDown className="chevron" />}
      </button>
      {isExpanded && (
        <ul className="group-features">
          {group.features.map((feat, idx) => (
            <li key={idx}>
              <FaCheck className="check-icon" />
              <span>{feat}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BundleCard({ bundle, billingCycle, isSelected, onSelect }) {
  const [expandedGroups, setExpandedGroups] = useState(() => bundle.featureGroups.map((_, idx) => idx)); // expanded by default
  
  const toggleGroup = (idx) => {
    setExpandedGroups(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };
  
  const expandAll = (e) => {
    e.stopPropagation();
    setExpandedGroups(bundle.featureGroups.map((_, idx) => idx));
  };
  
  const collapseAll = (e) => {
    e.stopPropagation();
    setExpandedGroups([]);
  };
  
  const handleCardClick = () => {
    onSelect(bundle);
  };
  
  // Separate inherited groups from regular groups
  const inheritedGroup = bundle.featureGroups.find(g => g.isInherited);
  const regularGroups = bundle.featureGroups
    .filter(g => !g.isInherited)
    // Sort by number of features (descending - more features first)
    .sort((a, b) => (b.features?.length || 0) - (a.features?.length || 0));
  
  return (
    <div 
      className={`bundle-card ${bundle.popular ? 'popular' : ''} ${isSelected ? 'selected' : ''}`}
      onClick={handleCardClick}
    >
      {bundle.popular && <div className="popular-badge">Most Popular</div>}
      {isSelected && (
        <div className="bundle-selected-badge" aria-label="Selected plan">
          <FaCheck />
          <span>Selected</span>
        </div>
      )}
      
      <div className="bundle-header">
        <h3>{bundle.name}</h3>
        <p className="bundle-desc">{bundle.description}</p>
       {/*  <div className="bundle-price">
          <span className="price-amount">Enterprise</span>
          <span className="price-period">Contact sales</span>
        </div> */}
        <span className="bundle-ideal">{bundle.ideal}</span>
      </div>
      
      <div className="bundle-features">
        <div className="features-controls">
          <button onClick={expandAll}>Expand all</button>
          <span className="divider">|</span>
          <button onClick={collapseAll}>Collapse all</button>
        </div>
        
        <div className="feature-groups-grid">
          {regularGroups.map((group, idx) => (
            <FeatureGroup
              key={idx}
              group={group}
              isExpanded={expandedGroups.includes(bundle.featureGroups.indexOf(group))}
              onToggle={() => toggleGroup(bundle.featureGroups.indexOf(group))}
            />
          ))}
        </div>
        
        {inheritedGroup && (
          <div className="feature-group inherited at-bottom">
            <span className="inherited-label">{inheritedGroup.name}</span>
          </div>
        )}
      </div>
      
      <button 
        className={`bundle-cta ${bundle.popular ? 'primary-btn' : 'secondary-btn'}`}
        style={{ color: '#fff' }}
        onClick={(e) => { e.stopPropagation(); onSelect(bundle); }}
      >
        {isSelected ? 'Selected' : 'Choose Plan'}
      </button>
    </div>
  );
}

function AddOnCard({ addon, isSelected, isIncluded, onToggle, billingCycle }) {
  const handleClick = () => {
    // Don't allow toggling if already included in bundle
    if (!isIncluded) {
      onToggle(addon.id);
    }
  };
  
  return (
    <div 
      className={`addon-card ${isSelected ? 'selected' : ''} ${isIncluded ? 'included' : ''}`} 
      onClick={handleClick}
    >
      <div className="addon-checkbox">
        {(isSelected || isIncluded) && <FaCheck />}
      </div>
      <div className="addon-info">
        <div className="addon-header">
          <h4>{addon.name}</h4>
          {isIncluded ? (
            <span className="addon-price included-badge">Included</span>
          ) : (
            <span className="addon-price">Add-on module</span>
          )}
        </div>
        <p className="addon-desc">{addon.description}</p>
        <ul className="addon-features">
          {addon.features.map((feat, idx) => (
            <li key={idx}>{feat}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PricingSummary({
  selectedBundle,
  selectedAddons,
  onOpenModal
}) {
  const includedAddonIds = selectedBundle?.includedAddons || [];
  const includedAddons = ADD_ONS.filter(a => includedAddonIds.includes(a.id));
  const selectedAddonOnlyIds = selectedAddons.filter(id => !includedAddonIds.includes(id));
  const selectedAddonOnly = ADD_ONS.filter(a => selectedAddonOnlyIds.includes(a.id));
  const additionalAddons = ADD_ONS.filter(
    a => !includedAddonIds.includes(a.id) && !selectedAddonOnlyIds.includes(a.id)
  );

  return (
    <div className="pricing-summary">
      <h3>Your Plan</h3>
      
      {selectedBundle ? (
        <>
          <div className="summary-bundle">
            <span className="summary-label">{selectedBundle.name}</span>
            <span className="summary-value">Enterprise pricing</span>
          </div>

          <div className="summary-meta">
            <div className="summary-row">
              <span className="summary-label">Included modules</span>
              <span className="summary-value">{includedAddons.length}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Selected modules</span>
              <span className="summary-value">{selectedAddonOnly.length}</span>
            </div>
          </div>

          {(includedAddons.length > 0 || selectedAddonOnly.length > 0) && (
            <div className="summary-modules">
              {includedAddons.length > 0 && (
                <div className="summary-section">
                  <div className="summary-section-title">Included</div>
                  <ul className="summary-module-list">
                    {includedAddons.map(a => (
                      <li key={a.id} className="summary-module-item">
                        <span className="summary-dot" aria-hidden="true" />
                        <span className="summary-module-name">{a.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedAddonOnly.length > 0 && (
                <div className="summary-section">
                  <div className="summary-section-title">Selected</div>
                  <ul className="summary-module-list">
                    {selectedAddonOnly.map(a => (
                      <li key={a.id} className="summary-module-item">
                        <span className="summary-dot" aria-hidden="true" />
                        <span className="summary-module-name">{a.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="summary-divider" />

          <div className="hero-buttons summary-actions" style={{ justifyContent: 'center' }}>
            <button type="button" className="primary-btn summary-cta" onClick={() => onOpenModal('consultation')}>
              Request pricing
            </button>
            <button type="button" className="secondary-btn" onClick={() => onOpenModal('demo')}>
              Request a demo
            </button>
          </div>

        

          <p className="summary-note">Evaluation period available • Implementation support • Optional SLA</p>
          <p className="summary-fineprint">
            Per-seat pricing with role-based access: assign users as Field Staff or Supervisor/Admin. Not every seat gets every
            module, feature access depends on the user role.
          </p>
        </>
      ) : (
        <div className="summary-empty">
          <p>Select a plan to see the recommended package</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function PricingNew() {
  const seoConfig = useMemo(() => {
    const title = 'Enterprise Pricing | BloomNav';
    const description =
      'Enterprise pricing tailored to modules, seats, integrations, rollout plan, and SLA requirements. Request a pricing proposal or pilot briefing.';
    return {
      title,
      description,
      canonicalPath: '/pricing',
      ogType: 'website',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav',
      jsonLd: [
        {
          id: 'schema-pricing-page',
          data: {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: title,
            description,
            url: `${window.location.origin}/pricing`,
            isPartOf: {
              '@type': 'WebSite',
              name: 'BloomNav',
              url: `${window.location.origin}/`
            }
          }
        },
        {
          id: 'schema-pricing-faq',
          data: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How is BloomNav priced?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text:
                    'Pricing is scoped to modules, seats (by role), integrations, rollout plan, and support/SLA requirements. We provide a proposal after discovery.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can we start with an enterprise pilot?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text:
                    'Yes. We recommend a qualification-based pilot with defined success metrics before a large-scale rollout.'
                }
              }
            ]
          }
        }
      ]
    };
  }, []);

  useSeo(seoConfig);

  const [selectedBundle, setSelectedBundle] = useState(BUNDLES[1]); // Professional by default
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('consultation');
  const [modalPrefill, setModalPrefill] = useState(null);

  const buildPrefillMessage = useCallback((type) => {
    const includedAddonIds = selectedBundle?.includedAddons || [];
    const includedAddons = ADD_ONS.filter(a => includedAddonIds.includes(a.id)).map(a => a.name);
    const selectedAddonOnlyIds = selectedAddons.filter(id => !includedAddonIds.includes(id));
    const selectedAddonOnly = ADD_ONS.filter(a => selectedAddonOnlyIds.includes(a.id)).map(a => a.name);

    const lines = [];
    lines.push(type === 'consultation' ? 'Request: Enterprise pricing proposal' : 'Request: Product demo');
    lines.push(`Plan: ${selectedBundle?.name || 'Not selected'}`);
    if (includedAddons.length > 0) lines.push(`Included modules: ${includedAddons.join(', ')}`);
    if (selectedAddonOnly.length > 0) lines.push(`Selected modules: ${selectedAddonOnly.join(', ')}`);
    lines.push('');
    lines.push('Tell us about your business, issues, current workflows, and requirements:');
    lines.push('');
    lines.push('Seats / roles (optional):');
    lines.push('- Field staff:');
    lines.push('- Supervisors:');
    lines.push('- Admins:');
    lines.push('');
    lines.push('Integrations (optional): ERP / CRM / HRMS, SSO');
    lines.push('Timeline (optional):');
    lines.push('');
    lines.push('Notes:');
    return lines.join('\n');
  }, [selectedAddons, selectedBundle]);

  const openModal = (type) => {
    setModalType(type);
    setModalPrefill({ message: buildPrefillMessage(type) });
    setModalOpen(true);
  };
  
  const toggleAddon = useCallback((addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  }, []);
  
  return (
    <div className="home-container pricing-page-simple">
      {/* Hero */}
      <section className="pricing-hero">
        <h1>Enterprise pricing</h1>
        <p>Pricing is tailored to modules, seats, integrations, rollout plan, and SLA requirements.</p>
      </section>

      {/* How pricing works */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">How pricing works</h2>
          <p className="section-subtitle">We scope pricing to match governance, deployment, and operational requirements.</p>
        </div>
        <div className="applications-grid">
          <div className="application-item">
            <h4>Modules</h4>
            <p>Select the platform modules you need across operations and finance.</p>
          </div>
          <div className="application-item">
            <h4>Seats</h4>
            <p>License seats by role (field staff, supervisors, admins) to match access controls.</p>
          </div>
          <div className="application-item">
            <h4>Integrations & SLA</h4>
            <p>ERP/CRM/HRMS integrations and optional SLA are scoped based on rollout and support needs.</p>
          </div>
        </div>
      </section>
      
      {/* Plans + Summary */}
      <section className="plans-section">
        <div className="plans-wrapper">
          <div className="plans-grid">
            {BUNDLES.map(bundle => (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                isSelected={selectedBundle?.id === bundle.id}
                onSelect={setSelectedBundle}
              />
            ))}
          </div>
          
          <div className="plans-summary">
            <PricingSummary
              selectedBundle={selectedBundle}
              selectedAddons={selectedAddons}
              onOpenModal={openModal}
            />
          </div>
        </div>
      </section>
      
      {/* Add-ons */}
      <section className="addons-section">
        <div className="addons-header">
          <h2>Modules</h2>
          <p>Add modules to match your workflow and compliance requirements.</p>
        </div>
        
        <div className="addons-grid">
          {ADD_ONS.map(addon => {
            const isIncluded = selectedBundle?.includedAddons?.includes(addon.id) || false;
            return (
              <AddOnCard
                key={addon.id}
                addon={addon}
                isSelected={selectedAddons.includes(addon.id)}
                isIncluded={isIncluded}
                onToggle={toggleAddon}
              />
            );
          })}
        </div>
      </section>

      {/* Custom Quote */}
      <section className="section pricing-custom-quote">
        <div className="section-header">
          <h2 className="section-title">Need a Custom Solution?</h2>
          <p className="section-subtitle">
            Need a custom solution built for your business, optimise machine operations or integrating with an ERP / CRM / HRMS?<br/> We offer tailored
            implementation, custom workflows, integrations, and enterprise rollout support.
          </p>
        </div>

        <div className="custom-quote-inner">
          <div className="application-item custom-quote-card">
            <h3>Enterprise Integrations & Rollout</h3>
            <p>
              Tell us what systems you’re using and what outcomes you need. We’ll scope the work and share a tailored
              quote, timeline, and rollout plan.
            </p>
            <div className="hero-buttons">
              <button type="button" className="primary-btn" onClick={() => openModal('consultation')}>
                Request a Custom Quote
              </button>
              <a
                className="secondary-btn"
                href="mailto:support@bloomnav.com?subject=Custom%20Quote%20Request%20(ERP%2FCRM%2FHRMS)"
                style={{ textDecoration: 'none' }}
              >
                Email Sales
              </a>
            </div>
          </div>

          <div className="application-item custom-quote-card">
            <h3>Common Requests</h3>
            <ul>
              <li>API integrations with ERP/CRM/HRMS</li>
              <li>Single Sign-On (SSO) & provisioning</li>
              <li>Custom reports, exports, and dashboards</li>
              <li>Data migration & onboarding support</li>
              <li>Custom workflows & approvals</li>
            </ul>
          </div>
        </div>
      </section>
      
      {/* FAQ */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>What’s included?</h4>
            <p>Mobile apps, core admin controls, and implementation guidance are included. Optional SLA is available for enterprise deployments.</p>
          </div>
          <div className="faq-item">
            <h4>Can we start with a pilot?</h4>
            <p>Yes. We recommend a qualification-based pilot with clear success metrics before large-scale rollout.</p>
          </div>
          <div className="faq-item">
            <h4>Is an evaluation available?</h4>
            <p>Yes. An evaluation period is available for qualified organizations. Contact sales to align on scope.</p>
          </div>
          <div className="faq-item">
            <h4>How does pricing scale?</h4>
            <p>Pricing scales with seats, modules, integrations, and support requirements. We provide a proposal after discovery.</p>
          </div>
        </div>
      </section>
      
      <ConsultationModal
        isOpen={modalOpen}
        modalType={modalType}
        onClose={() => setModalOpen(false)}
        prefill={modalPrefill}
      />
    </div>
  );
}

export default PricingNew;
